/**
 * StudyPage — /contracts/studies/:studyId (WP-103, decision (290), 2026-09-29).
 * Replaces the study detail modal: a page with a breadcrumb (Studies >
 * protocol), the protocol as its header, and StudyDetail as the body. It loads
 * the study by the id in the URL (GET /studies/:id), so a direct load and the
 * browser's Back button work. A 400 or 404 (unknown id, or another site's
 * study) or a response with no study is "not found"; any other failure is a
 * load error. Both link back to Studies.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FlaskConical, SearchX } from 'lucide-react';
import { PageHeader, EmptyState, secondaryBtnCls } from '../../../ui';
import { useContracts } from '../../ContractsProvider';
import { getStudy } from '../../api/client';
import type { Study } from '../../api/client';
import { StudiesBreadcrumb } from './StudiesBreadcrumb';
import { StudyDetail } from './StudyDetail';

type LoadStatus = 'loading' | 'ready' | 'notFound' | 'error';

const STUDIES_CRUMB = { label: 'Studies', to: '/contracts/studies' };

export function StudyPage() {
  const { studyId } = useParams();
  const { siteId } = useContracts();
  const [study, setStudy] = useState<Study | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');

  useEffect(() => {
    if (!siteId || !studyId) return;
    let cancelled = false;
    setStatus('loading');
    setStudy(null);
    getStudy(studyId)
      .then((loaded) => {
        if (cancelled) return;
        if (!loaded) {
          setStatus('notFound');
          return;
        }
        setStudy(loaded);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const httpStatus = (err as { response?: { status?: number } })?.response?.status;
        setStatus(httpStatus === 400 || httpStatus === 404 ? 'notFound' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, studyId]);

  const backToStudies = (
    <Link data-testid="studies-not-found-back" to="/contracts/studies" className={secondaryBtnCls}>
      Back to Studies
    </Link>
  );

  if (status === 'ready' && study) {
    return (
      <div data-testid="study-page" className="space-y-4">
        <StudiesBreadcrumb items={[STUDIES_CRUMB, { label: study.protocol_number }]} />
        <PageHeader title={study.protocol_number} icon={FlaskConical} />
        <StudyDetail study={study} onStudyUpdated={(updated) => setStudy((prev) => ({ ...updated, tags: updated.tags ?? prev?.tags ?? study.tags }))} />
      </div>
    );
  }

  if (status === 'notFound') {
    return (
      <div data-testid="study-page" className="space-y-4">
        <StudiesBreadcrumb items={[STUDIES_CRUMB, { label: 'Not found' }]} />
        <div data-testid="study-not-found">
          <EmptyState
            icon={SearchX}
            title="Study not found"
            hint="It may have been removed, or it belongs to another site."
            variant="card"
            action={backToStudies}
          />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div data-testid="study-page" className="space-y-4">
        <StudiesBreadcrumb items={[STUDIES_CRUMB, { label: 'Error' }]} />
        <div data-testid="study-load-error">
          <EmptyState icon={SearchX} title="Could not load this study." variant="card" action={backToStudies} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="study-page" className="space-y-4">
      <StudiesBreadcrumb items={[STUDIES_CRUMB, { label: 'Loading…' }]} />
      <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
    </div>
  );
}

export default StudyPage;
